import React, { useState, useCallback } from "react"
import {
  Upload,
  Button,
  message,
  Image,
  Tag,
  Popconfirm,
  Tooltip,
  Typography,
  Space,
  Card,
  Row,
  Col,
} from "antd"
import {
  InboxOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  PlusOutlined,
  EyeOutlined,
  PictureOutlined,
} from "@ant-design/icons"
import type { RcFile } from "antd/es/upload/interface"
import fileStorageService, { PRODUCT_IMAGES_BUCKET } from "../../services/fileStorageService"
import { useConfig } from "../../contexts/ConfigContext"
import { useCompany } from "../../contexts/CompanyContext"
import useAuth from "../../hooks/useAuth"
import { TStorageElementImage } from "../../models/storageModel"

const { Text } = Typography
const { Dragger } = Upload

const ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/svg+xml",
])
const MAX_FILE_SIZE_MB = 5
const MAX_IMAGES_COUNT = 10

interface ItemImageUploaderProps {
  elementId?: number
  elementUid?: string
  value?: TStorageElementImage[]
  onChange?: (images: TStorageElementImage[], primaryUrl?: string) => void
  disabled?: boolean
}

export const ItemImageUploader: React.FC<ItemImageUploaderProps> = ({
  elementId,
  elementUid,
  value = [],
  onChange,
  disabled = false,
}) => {
  const { config } = useConfig()
  const { company } = useCompany()
  const { tokenData } = useAuth.useToken("authCsrfToken", config.session, config.key)
  const [uploading, setUploading] = useState(false)
  const [deletingPath, setDeletingPath] = useState<string | null>(null)

  const imagesList = Array.isArray(value) ? value : []

  const updateImages = useCallback(
    (newList: TStorageElementImage[]) => {
      // Ensure at least one image is primary if list is not empty
      let hasPrimary = newList.some((img) => img.is_primary)
      const sanitizedList = newList.map((img, idx) => {
        if (!hasPrimary && idx === 0) {
          hasPrimary = true
          return { ...img, is_primary: true, display_order: idx }
        }
        return { ...img, display_order: idx }
      })

      const primaryImg = sanitizedList.find((img) => img.is_primary) || sanitizedList[0]
      const primaryUrl = primaryImg?.file_url || ""
      onChange?.(sanitizedList, primaryUrl)
    },
    [onChange]
  )

  const beforeUpload = useCallback(
    (file: RcFile, fileList: RcFile[]) => {
      if (!ALLOWED_TYPES.has(file.type)) {
        message.error(`"${file.name}" no es un formato válido (solo JPG, PNG, WebP, SVG, GIF).`)
        return Upload.LIST_IGNORE
      }
      const sizeMB = file.size / 1024 / 1024
      if (sizeMB > MAX_FILE_SIZE_MB) {
        message.error(`"${file.name}" supera el límite de ${MAX_FILE_SIZE_MB} MB.`)
        return Upload.LIST_IGNORE
      }
      if (imagesList.length + fileList.length > MAX_IMAGES_COUNT) {
        message.warning(`Solo se permite un máximo de ${MAX_IMAGES_COUNT} imágenes por producto.`)
        return Upload.LIST_IGNORE
      }
      return true
    },
    [imagesList.length]
  )

  const handleUploadFiles = useCallback(
    async (file: File) => {
      if (!company.current?.uid) {
        message.error("No se pudo identificar la empresa actual")
        return
      }

      const timestamp = Date.now()
      const sanitizedName = file.name.replaceAll(/[^a-zA-Z0-9._-]/g, "_")
      const folder = elementUid || (elementId ? `item_${elementId}` : `new_${timestamp}`)
      const storagePath = `${company.current.uid}/products/${folder}/${timestamp}_${sanitizedName}`

      try {
        setUploading(true)
        const { url, storagePath: finalPath } = await fileStorageService.uploadFile(
          tokenData,
          PRODUCT_IMAGES_BUCKET,
          storagePath,
          file,
          true
        )

        const isFirst = imagesList.length === 0
        const newImage: TStorageElementImage = {
          element_id: elementId,
          uid: `img_${timestamp}_${Math.random().toString(36).substring(2, 6)}`,
          file_name: file.name,
          file_url: url,
          storage_path: finalPath,
          is_primary: isFirst,
          display_order: imagesList.length,
        }

        const nextList = [...imagesList, newImage]
        updateImages(nextList)
        message.success(`Imagen "${file.name}" cargada correctamente`)
      } catch (err: any) {
        message.error(err.message || "Error al subir la imagen")
      } finally {
        setUploading(false)
      }
    },
    [company, elementUid, elementId, tokenData, imagesList, updateImages]
  )

  const handleSetPrimary = (index: number) => {
    const updated = imagesList.map((img, idx) => ({
      ...img,
      is_primary: idx === index,
    }))
    updateImages(updated)
    message.success("Imagen de portada actualizada")
  }

  const handleDeleteImage = async (index: number) => {
    const target = imagesList[index]
    if (!target) return

    setDeletingPath(target.storage_path || target.file_url)
    try {
      if (target.storage_path) {
        await fileStorageService
          .deleteFile(tokenData, {
            bucket: PRODUCT_IMAGES_BUCKET,
            paths: [target.storage_path],
          })
          .catch(() => {})
      }

      const remaining = imagesList.filter((_, idx) => idx !== index)
      // If deleted image was primary and others exist, make the first remaining primary
      if (target.is_primary && remaining.length > 0) {
        remaining[0].is_primary = true
      }

      updateImages(remaining)
      message.success("Imagen eliminada")
    } catch (err: any) {
      message.error(err.message || "Error al eliminar la imagen")
    } finally {
      setDeletingPath(null)
    }
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Header informativo */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <Space size={6}>
          <PictureOutlined style={{ color: "#1890ff", fontSize: 16 }} />
          <Text strong style={{ fontSize: 13 }}>
            Galería de Imágenes del Producto
          </Text>
          <Tag color="default" style={{ borderRadius: 10, fontSize: 11 }}>
            {imagesList.length} / {MAX_IMAGES_COUNT}
          </Tag>
        </Space>
        <Text type="secondary" style={{ fontSize: 11 }}>
          JPG, PNG, WebP, SVG (Máx. 5MB)
        </Text>
      </div>

      {/* Grid de miniaturas cargadas */}
      {imagesList.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Image.PreviewGroup>
            <Row gutter={[12, 12]}>
              {imagesList.map((item, index) => {
                const isDeleting = deletingPath === (item.storage_path || item.file_url)
                return (
                  <Col xs={12} sm={8} md={6} key={item.uid || item.id || index}>
                    <Card
                      hoverable
                      size="small"
                      style={{
                        borderRadius: 8,
                        overflow: "hidden",
                        border: item.is_primary ? "2px solid #1890ff" : "1px solid #e8e8e8",
                        position: "relative",
                        background: "#fafafa",
                        boxShadow: item.is_primary ? "0 2px 8px rgba(24, 144, 255, 0.15)" : "none",
                      }}
                      styles={{
                        body: {
                          padding: "6px 8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          background: "#ffffff",
                        },
                      }}
                      cover={
                        <div
                          style={{
                            height: 120,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            background: "#fbfbfb",
                            overflow: "hidden",
                          }}
                        >
                          <Image
                            src={item.file_url}
                            alt={item.file_name}
                            style={{
                              width: "100%",
                              height: 120,
                              objectFit: "contain",
                              padding: 4,
                            }}
                            preview={{
                              mask: (
                                <Space>
                                  <EyeOutlined /> Ampliar
                                </Space>
                              ),
                            }}
                            fallback="assets/images/no-data-image.svg"
                          />

                          {item.is_primary && (
                            <div
                              style={{
                                position: "absolute",
                                top: 6,
                                left: 6,
                                zIndex: 2,
                              }}
                            >
                              <Tag
                                color="gold"
                                icon={<StarFilled />}
                                style={{
                                  borderRadius: 4,
                                  fontWeight: 600,
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                                  margin: 0,
                                  fontSize: 11,
                                }}
                              >
                                Portada
                              </Tag>
                            </div>
                          )}
                        </div>
                      }
                    >
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: 4 }}>
                        <Text style={{ fontSize: 11, color: "#595959" }} title={item.file_name}>
                          {item.file_name || `Foto ${index + 1}`}
                        </Text>
                      </div>

                      {!disabled && (
                        <Space size={2}>
                          {!item.is_primary && (
                            <Tooltip title="Establecer como foto de portada">
                              <Button
                                type="text"
                                size="small"
                                icon={<StarOutlined style={{ color: "#faad14" }} />}
                                onClick={() => handleSetPrimary(index)}
                                style={{ padding: "0 4px", height: 24 }}
                              />
                            </Tooltip>
                          )}

                          <Popconfirm
                            title="¿Eliminar esta imagen?"
                            onConfirm={() => handleDeleteImage(index)}
                            okText="Sí, eliminar"
                            cancelText="Cancelar"
                            placement="topRight"
                          >
                            <Tooltip title="Eliminar imagen">
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                loading={isDeleting}
                                style={{ padding: "0 4px", height: 24 }}
                              />
                            </Tooltip>
                          </Popconfirm>
                        </Space>
                      )}
                    </Card>
                  </Col>
                )
              })}
            </Row>
          </Image.PreviewGroup>
        </div>
      )}

      {/* Zona de Carga (Dragger o Botón) */}
      {!disabled && imagesList.length < MAX_IMAGES_COUNT && (
        <Dragger
          multiple
          accept=".jpg,.jpeg,.png,.webp,.gif,.svg"
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={({ file }) => handleUploadFiles(file as File)}
          disabled={uploading}
          style={{
            background: "#fafafa",
            border: "1px dashed #d9d9d9",
            borderRadius: 8,
            padding: imagesList.length > 0 ? "12px 16px" : "20px 16px",
          }}
        >
          {imagesList.length === 0 ? (
            <div>
              <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                <InboxOutlined style={{ color: "#1890ff", fontSize: 32 }} />
              </p>
              <p className="ant-upload-text" style={{ fontSize: 13, fontWeight: 500, margin: "0 0 4px" }}>
                Haz clic o arrastra imágenes aquí para agregarlas al catálogo
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 11, color: "#8c8c8c", margin: 0 }}>
                Puedes subir hasta {MAX_IMAGES_COUNT} imágenes (la primera será la portada por defecto)
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <PlusOutlined style={{ color: "#1890ff" }} />
              <span style={{ fontSize: 12, color: "#1890ff", fontWeight: 500 }}>
                {uploading ? "Subiendo imágenes..." : "Agregar más imágenes"}
              </span>
            </div>
          )}
        </Dragger>
      )}
    </div>
  )
}

export default ItemImageUploader
